import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { locationsAPI, bookingsAPI, getServerTime } from '../../api/bookings';
import { bruneiToday, bruneiDateOffset } from '../../lib/brunei';
import { DEFAULT_SLOTS, getDayOfWeek, isSlotInBreak } from './utils';
import { useAuth } from '../../context/AuthContext';

export function useLandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState({});
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(false);

  const [serverDate, setServerDate] = useState('');
  const [serverTime, setServerTime] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  // ---------- derived ----------
  const getBreakInfo = useMemo(() => {
    if (!selectedLocation || !selectedDate) return null;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh?.breaks?.length) return null;
    return oh.breaks;
  }, [selectedLocation, selectedDate]);

  const timeSlots = useMemo(() => {
    if (!selectedLocation || !selectedDate) return DEFAULT_SLOTS;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh || oh.closed) return [];

    const openTime = oh.open || '08:00';
    const closeTime = oh.close || '22:00';
    const breaks = oh.breaks || [];
    const slots = [];
    const [openHour] = openTime.split(':').map(Number);
    let [closeHour] = closeTime.split(':').map(Number);
    if (closeHour === 0) closeHour = 24;

    for (let hour = openHour; hour < closeHour; hour++) {
      const startHour = hour.toString().padStart(2, '0');
      const slotStart = `${startHour}:00`;
      if (isSlotInBreak(slotStart, breaks)) continue;
      const endHour = ((hour + 1) % 24).toString().padStart(2, '0');
      slots.push(`${startHour}:00-${endHour}:00`);
    }
    return slots;
  }, [selectedLocation, selectedDate]);

  const isLocationClosed = useMemo(() => {
    if (!selectedLocation || !selectedDate) return false;
    const dayOfWeek = getDayOfWeek(selectedDate);
    return selectedLocation.operating_hours?.[dayOfWeek]?.closed === true;
  }, [selectedLocation, selectedDate]);

  const getOperatingHoursDisplay = useCallback(() => {
    if (!selectedLocation || !selectedDate) return null;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh || oh.closed) return 'Closed';
    const closeDisplay = oh.close === '00:00' ? '24:00 (Midnight)' : oh.close;
    return `${oh.open} - ${closeDisplay}`;
  }, [selectedLocation, selectedDate]);

  // ---------- effects ----------
  const loadServerTime = useCallback(async () => {
    try {
      const t = await getServerTime();
      setServerDate(t.date);
      setServerTime(t.time.substring(0, 5));
      setMinDate(t.date);
      setSelectedDate(t.date);
      const maxDateObj = new Date(t.date);
      maxDateObj.setMonth(maxDateObj.getMonth() + 1);
      setMaxDate(maxDateObj.toISOString().split('T')[0]);
    } catch (error) {
      console.error('Error loading server time:', error);
      const today = bruneiToday();
      setSelectedDate(today);
      setMinDate(today);
      setMaxDate(bruneiDateOffset(30));
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      setLocations(locs);

      const saved = localStorage.getItem('smashzone_selection');
      if (saved) {
        try {
          const selection = JSON.parse(saved);
          const hoursDiff = (new Date() - new Date(selection.savedAt)) / 3600000;
          if (hoursDiff < 24) {
            const savedLoc = locs.find((l) => l.id === selection.locationId);
            if (savedLoc) setSelectedLocation(savedLoc);
            else if (locs.length > 0) setSelectedLocation(locs[0]);
            if (selection.date && selection.date >= minDate) setSelectedDate(selection.date);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved selection:', e);
        }
      }
      if (locs.length > 0) setSelectedLocation(locs[0]);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
    // `minDate` intentionally excluded — first load is fine with ''.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailabilityAndPricing = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) return;
    setLoading(true);
    const availData = {};
    const pricingData = {};
    try {
      await Promise.all(timeSlots.map(async (slot) => {
        try {
          const avail = await bookingsAPI.checkAvailability(selectedLocation.id, selectedDate, slot);
          availData[slot] = avail;
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/pricing/calculate?` +
            `location_id=${selectedLocation.id}&date=${selectedDate}&time_slot=${slot}&sport=badminton&duration=1`
          );
          pricingData[slot] = await response.json();
        } catch (error) {
          availData[slot] = { available_courts: 0, total_courts: 5 };
          pricingData[slot] = { price_per_hour: 14 };
        }
      }));
      setAvailability(availData);
      setPricing(pricingData);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  useEffect(() => {
    loadServerTime();
    loadLocations();
  }, [loadServerTime, loadLocations]);

  // NOTE: `smashzone_selection` is deliberately NOT auto-saved on every
  // selectedLocation/selectedDate change here — that used to re-arm the
  // "Restored your previous selection" toast on every passive Home-page
  // visit (default location + today's date getting re-persisted with a
  // fresh `savedAt` each time), even when the customer never expressed a
  // real booking intent. It's only saved on an explicit action now, inside
  // `handleBookSlot` below.
  useEffect(() => {
    if (selectedLocation && selectedDate) loadAvailabilityAndPricing();
  }, [selectedLocation, selectedDate, loadAvailabilityAndPricing]);

  // ---------- handlers ----------
  const handleLocationChange = useCallback((locationId) => {
    const location = locations.find((l) => l.id === locationId);
    setSelectedLocation(location);
    setAvailability({});
    setPricing({});
  }, [locations]);

  const isTimeSlotPast = useCallback((timeSlot) => {
    if (!serverDate || !serverTime) return false;
    if (selectedDate > serverDate) return false;
    if (selectedDate < serverDate) return true;
    const slotStartTime = timeSlot.split('-')[0];
    return slotStartTime <= serverTime;
  }, [serverDate, serverTime, selectedDate]);

  const handleBookSlot = useCallback((timeSlot) => {
    if (isTimeSlotPast(timeSlot)) {
      toast.error('This time slot has already passed');
      return;
    }
    const avail = availability[timeSlot];
    const isFullyBooked = avail && avail.available_courts === 0;
    const sessionSelection = {
      locationId: selectedLocation.id,
      locationName: selectedLocation.name,
      date: selectedDate,
      timeSlot,
      isFullyBooked,
      waitlistCount: avail?.waitlist_count || 0,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem('smashzone_selection', JSON.stringify(sessionSelection));

    if (!user) {
      const intentMessage = isFullyBooked
        ? 'This slot is fully booked. Login to join the waitlist.'
        : 'Login to complete your booking';
      toast.info(isFullyBooked ? 'Please login to join the waitlist' : 'Please login to continue booking');
      localStorage.setItem('bookingIntent', JSON.stringify({
        ...sessionSelection,
        action: isFullyBooked ? 'joinWaitlist' : 'book',
        message: intentMessage,
      }));
      navigate('/auth');
    } else {
      navigate('/book');
    }
  }, [isTimeSlotPast, availability, selectedLocation, selectedDate, navigate, user]);

  return {
    navigate,
    locations, selectedLocation, selectedDate, availability, pricing,
    loading, minDate, maxDate,
    setSelectedDate,
    timeSlots, getBreakInfo, isLocationClosed, getOperatingHoursDisplay,
    handleLocationChange, isTimeSlotPast, handleBookSlot,
  };
}
